import { Bubble } from 'react-chartjs-2'

import {
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Tooltip
} from 'chart.js'

ChartJS.register(LinearScale, LogarithmicScale, PointElement, Tooltip, Legend)

export interface BubbleChartItem {
  x: number
  y: number
  r: number
  label: string
  color: string
  opacity?: number
}

interface Props {
  items: BubbleChartItem[]
  xTitle?: string
  yTitle?: string
  yMax?: number
  logX?: boolean
  // 各バブルの上にラベル（item.label）を常時描画するか（false時はツールチップのタイトルとしてのみ使用）
  showLabels?: boolean
  // 各バブルにマウスオーバーした際の追加表示行
  tooltipLines?: (item: BubbleChartItem) => string[]
  onItemClick?: (index: number) => void
}

// 各バブルの上にラベルを常時表示するプラグイン
const bubbleLabelPlugin = {
  id: 'bubbleLabel',
  afterDatasetsDraw: (chart: ChartJS<'bubble'>) => {
    const meta = chart.getDatasetMeta(0)
    const items = (chart.data.datasets[0]?.data ?? []) as unknown as BubbleChartItem[]
    const { ctx } = chart
    ctx.save()
    ctx.font = "600 10px 'Noto Sans JP', sans-serif"
    ctx.fillStyle = '#34414F'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    meta.data.forEach((element, i) => {
      const item = items[i]
      if (!item?.label) return
      ctx.fillText(
        item.label,
        element.x,
        element.y - ((element as { options?: { radius?: number } }).options?.radius ?? 0) - 4
      )
    })
    ctx.restore()
  }
}

const BubbleChart = ({
  items,
  xTitle,
  yTitle,
  yMax,
  logX = false,
  showLabels = true,
  tooltipLines,
  onItemClick
}: Props) => {
  const options: ChartOptions<'bubble'> = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      if (onItemClick && elements.length > 0) {
        onItemClick(elements[0].index)
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(2, 6, 23, 0.95)',
        titleFont: { size: 12, weight: 'bold' },
        bodyFont: { size: 11 },
        padding: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          title: (ctx) => (ctx[0]?.raw as BubbleChartItem)?.label ?? '',
          label: (ctx) => (tooltipLines ? tooltipLines(ctx.raw as BubbleChartItem) : [])
        }
      }
    },
    scales: {
      x: {
        type: logX ? 'logarithmic' : 'linear',
        title: xTitle
          ? { display: true, text: xTitle, font: { size: 11 }, color: '#34414F' }
          : undefined,
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 10 }, color: '#34414F' }
      },
      y: {
        beginAtZero: true,
        max: yMax,
        title: yTitle
          ? { display: true, text: yTitle, font: { size: 11 }, color: '#34414F' }
          : undefined,
        border: { display: false },
        grid: { color: '#E4E8EE' },
        ticks: { font: { size: 10 }, color: '#34414F', stepSize: 1 }
      }
    }
  }

  return (
    <Bubble
      data={{
        datasets: [
          {
            data: items,
            backgroundColor: items.map(
              (item) =>
                item.color +
                Math.round((item.opacity ?? 0.6) * 255)
                  .toString(16)
                  .padStart(2, '0')
            ),
            borderColor: items.map((item) => item.color),
            borderWidth: 1
          }
        ]
      }}
      options={options}
      plugins={showLabels ? [bubbleLabelPlugin] : []}
    />
  )
}

export default BubbleChart
